import {
  Alert,
  Box,
  Button,
  ColumnLayout,
  Container,
  Header,
  KeyValuePairs,
  Link,
  Modal,
  SpaceBetween,
  StatusIndicator,
  Toggle,
} from '@cloudscape-design/components';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { useState } from 'react';

import {
  createScorecardTrackingProfile,
  getScorecardTrackingProfileForPlace,
  updateScorecardTrackingProfile,
} from '../../common/api/scorecardTracking';
import type { PlaceDetail } from '../../common/types';
import { trackingStatusLabel } from '../../views/dashboard-scorecard-watchlist';

export type ScorecardTrackingPanelProps = {
  place: PlaceDetail;
};

export function ScorecardTrackingPanel({ place }: ScorecardTrackingPanelProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [ongoingEnabled, setOngoingEnabled] = useState(true);
  const [pauseOpen, setPauseOpen] = useState(false);
  const profileQuery = useQuery({
    queryKey: ['scorecard-watchlist-by-place', place.uuid],
    queryFn: () => getScorecardTrackingProfileForPlace(place.uuid),
    refetchInterval: (query) =>
      query.state.data &&
      ['initializing', 'backfilling', 'refreshing'].includes(query.state.data.status)
        ? 5_000
        : false,
  });

  const createMutation = useMutation({
    mutationFn: () =>
      createScorecardTrackingProfile({
        place_uuid: place.uuid,
        ongoing_enabled: ongoingEnabled,
      }),
    onSuccess: async () => {
      setConfirmOpen(false);
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: ['scorecard-watchlist-by-place', place.uuid],
        }),
        queryClient.invalidateQueries({ queryKey: ['scorecard-watchlist'] }),
      ]);
    },
  });
  const updateMutation = useMutation({
    mutationFn: ({ uuid, enabled }: { uuid: string; enabled: boolean }) =>
      updateScorecardTrackingProfile(uuid, { ongoing_enabled: enabled }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['scorecard-watchlist-by-place', place.uuid],
      });
    },
  });

  const profile = profileQuery.data;
  const active = profile
    ? ['initializing', 'backfilling', 'refreshing'].includes(profile.status)
    : false;
  const latestMetric = (profile?.latest_snapshot?.metrics ?? []).find(
    (item) => item.source === 'google' && item.window_days === 30,
  );
  const progressRun = profile?.active_run ?? profile?.latest_run;

  return (
    <>
      <Modal
        visible={confirmOpen}
        header="Build Scorecard History"
        onDismiss={() => !createMutation.isPending && setConfirmOpen(false)}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
              <Button
                variant="primary"
                loading={createMutation.isPending}
                onClick={() => createMutation.mutate()}
              >
                Confirm and start tracking
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        <SpaceBetween size="m">
          <Alert type="info">
            This queues a 545-day review-source backfill to construct twelve monthly comparison
            points with complete 30, 60, 90, and 180-day windows.
          </Alert>
          <KeyValuePairs
            columns={2}
            items={[
              { label: 'Business', value: place.name },
              { label: 'Address', value: place.address },
              { label: 'Business type', value: 'Inferred from Google Place categories' },
              {
                label: 'Review sources',
                value: 'Google plus applicable industry sources after trusted matching',
              },
            ]}
          />
          <Toggle
            checked={ongoingEnabled}
            onChange={({ detail }) => setOngoingEnabled(detail.checked)}
          >
            Continue refreshing every 30 days when the initial history is ready
          </Toggle>
          <Box color="text-body-secondary">
            A friendly private-by-link route under <code>/h/</code> is published after the first
            usable snapshot and can be disabled later.
          </Box>
        </SpaceBetween>
      </Modal>

      <Modal
        visible={pauseOpen}
        header="Pause monthly Scorecard tracking?"
        onDismiss={() => !updateMutation.isPending && setPauseOpen(false)}
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button onClick={() => setPauseOpen(false)}>Keep tracking</Button>
              <Button
                variant="primary"
                loading={updateMutation.isPending}
                onClick={() => {
                  if (!profile) return;
                  updateMutation.mutate(
                    { uuid: profile.uuid, enabled: false },
                    { onSuccess: () => setPauseOpen(false) },
                  );
                }}
              >
                Pause tracking
              </Button>
            </SpaceBetween>
          </Box>
        }
      >
        Existing snapshots and the public history remain available. Scheduled 30-day refreshes
        stop until you resume tracking.
      </Modal>

      <Container
        header={
          <Header
            variant="h2"
            description="Track scorecard health over time, rolling periods, source mix, and rating targets."
            actions={
              profile ? (
                <Button
                  onClick={() =>
                    navigate({
                      to: '/dashboard/scorecard/watchlist/$uuid',
                      params: { uuid: profile.uuid },
                    })
                  }
                >
                  Open tracking detail
                </Button>
              ) : (
                <Button
                  variant="primary"
                  iconName="add-plus"
                  loading={profileQuery.isLoading}
                  onClick={() => setConfirmOpen(true)}
                >
                  Build Scorecard History
                </Button>
              )
            }
          >
            Scorecard Watchlist
          </Header>
        }
      >
        {profile ? (
          <ColumnLayout columns={4} variant="text-grid">
            <div>
              <Box variant="awsui-key-label">Status</Box>
              <StatusIndicator
                type={
                  profile.status === 'ready'
                    ? 'success'
                    : profile.status === 'ready_partial'
                      ? 'warning'
                      : profile.status === 'failed'
                        ? 'error'
                        : active
                          ? 'in-progress'
                          : 'stopped'
                }
              >
                {trackingStatusLabel(profile.status)}
              </StatusIndicator>
            </div>
            <div>
              <Box variant="awsui-key-label">Current 30-day score</Box>
              <Box>
                {latestMetric?.composite_grade ?? '—'}
                {latestMetric?.composite_percentile == null
                  ? ''
                  : ` · ${Math.round(latestMetric.composite_percentile)}th percentile`}
              </Box>
              <Box color="text-body-secondary">
                Last: {profile.last_successful_run_at ? new Date(profile.last_successful_run_at).toLocaleString() : '—'}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">30-day refresh</Box>
              <Toggle
                checked={profile.ongoing_enabled}
                disabled={updateMutation.isPending || profile.status === 'archived'}
                onChange={({ detail }) =>
                  detail.checked
                    ? updateMutation.mutate({ uuid: profile.uuid, enabled: true })
                    : setPauseOpen(true)
                }
              >
                {profile.ongoing_enabled ? 'On' : 'Paused'}
              </Toggle>
              <Box color="text-body-secondary">
                Next: {profile.next_run_at ? new Date(profile.next_run_at).toLocaleString() : '—'}
              </Box>
            </div>
            <div>
              <Box variant="awsui-key-label">Sources / public history</Box>
              <Box>
                {(progressRun?.sources ?? []).length
                  ? (progressRun?.sources ?? [])
                      .map((source) => `${source.source}: ${source.status}`)
                      .join(', ')
                  : 'Waiting for source collection'}
              </Box>
              {profile.public_slug && profile.public_enabled && profile.published_at ? (
                <Link href={`/h/${profile.public_slug}`} external>
                  Open /h/{profile.public_slug}
                </Link>
              ) : (
                <Box color="text-body-secondary">Public page pending or disabled</Box>
              )}
            </div>
          </ColumnLayout>
        ) : (
          <Box color="text-body-secondary">
            This place is not being tracked longitudinally. Existing point-in-time scorecards are
            unchanged.
          </Box>
        )}
        {createMutation.isError || profileQuery.isError ? (
          <Alert type="error" header="Could not update Scorecard Watchlist">
            {(createMutation.error ?? profileQuery.error)?.message}
          </Alert>
        ) : null}
      </Container>
    </>
  );
}
